import { zodResolver } from "@hookform/resolvers/zod";
import type {
  AnalyticsView,
  Applicant,
  Application,
  Interview,
  InterviewStage,
  OfferApprovalRules,
  PropertyDefinition,
  PropertyValue,
} from "@shared/schema";
import { STATUS_LABELS } from "@shared/status-labels";
import type { KanbanApplication } from "@shared/views/job-pipeline/types";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Archive,
  ArrowLeft,
  Brain,
  Briefcase,
  Database,
  DollarSign,
  Link2,
  MoreHorizontal,
  Plus,
  RefreshCw,
  Search,
  Settings,
  SlidersHorizontal,
  User,
  UserCog,
  UserPlus,
  Users,
  X,
  XCircle,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useRoute } from "wouter";
import { z } from "zod";
import { AnalyticsViewEditor } from "@/components/analytics-view-editor";
import { ApplicantDetailModal } from "@/components/applicant-detail-modal";
import { JobAnalyticsTab } from "@/components/job-analytics-tab";
import { JobSettingsDialog } from "@/components/job-settings-dialog";
import { MoveConfirmationDialog } from "@/components/move-confirmation-dialog";
import {
  ImportFromExternalDatabaseButton,
  LinkToNotionButton,
  SyncNotionCandidatesButton,
} from "@/components/notion-sync-job";
import { PageHeader } from "@/components/page-header";
import { PropertyConfigSheet } from "@/components/property-config-sheet";
import { PropertyValueCell } from "@/components/property-value-cell";
import { StatusBadge } from "@/components/status-badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useAuth } from "@/hooks/use-auth";
import { useKanbanKeyboardNav } from "@/hooks/use-kanban-keyboard-nav";
import { useMoveApplication } from "@/hooks/use-move-application";
import { useQueryParams } from "@/hooks/use-query-params";
import { useToast } from "@/hooks/use-toast";
import {
  APPLICATION_TYPES,
  DEPARTMENTS,
  formatCurrency,
  getDepartmentLabel,
  getJobLevelLabel,
  JOB_LEVELS,
} from "@/lib/constants";
import { queryClient } from "@/lib/queryClient";
import { orpc, rpcClient } from "@/lib/rpc";
import { matchesSearch } from "@/lib/search-utils";
import type { PersonOption, StageInterviewersData } from "@/types/move-application";
import { JobPipelineKanbanView } from "@/views/job-pipeline/JobPipelineKanbanView";

type ApplicationWithDetails = Application & {
  applicant?: Applicant;
  interviews?: Interview[];
  owner?: { id: string; fullName: string };
  latestOfferStatus?: string;
  referenceChecksSummary?: { total: number; answered: number };
};

type KanbanResponse = {
  jobOpeningId: string;
  title: string;
  generalResponsibilities: string;
  desiredProfile: string;
  compensation: string;
  department: string;
  jobLevel: string;
  salaryExpected: string;
  status: string;
  hiddenFromCareers: boolean;
  allowAutoAdvance: boolean;
  notifyHmOnFeedback: boolean;
  notifyHmDailyDigest: boolean;
  notifyHmOnNewApplication: boolean;
  offerApprovalRules: OfferApprovalRules | null;
  notionId: string | null;
  hiringManager: { id: string; fullName: string } | null;
  canManage: boolean;
  interviewStages: InterviewStage[];
  stages: Record<string, (ApplicationWithDetails & { propertyValues?: PropertyValue[] })[]>;
  propertyDefinitions: PropertyDefinition[];
};

type KanbanColumn = {
  id: string;
  title: string;
  type: "fixed" | "stage";
  stageId?: string;
  archived?: boolean;
};

const applicationFormSchema = z
  .object({
    applicantName: z.string().min(2, "Nome é obrigatório"),
    applicantEmail: z.string().email("Email inválido").optional().or(z.literal("")),
    applicantPhone: z.string().optional(),
    linkedinUrl: z.string().url("URL inválida").optional().or(z.literal("")),
    type: z.enum(["organic", "referral", "hunting_comp", "consultoria"]),
  })
  .refine(
    (data) =>
      (data.applicantEmail && data.applicantEmail.length > 0) ||
      (data.applicantPhone && data.applicantPhone.length > 0) ||
      (data.linkedinUrl && data.linkedinUrl.length > 0),
    {
      message: "Informe pelo menos um contato: Email, Telefone ou LinkedIn",
      path: ["applicantEmail"],
    },
  );

type ApplicationFormValues = z.infer<typeof applicationFormSchema>;

const editJobFormSchema = z.object({
  title: z.string().min(2, "Título deve ter pelo menos 2 caracteres"),
  generalResponsibilities: z
    .string()
    .min(10, "Responsabilidades deve ter pelo menos 10 caracteres"),
  desiredProfile: z.string().min(10, "Perfil desejado deve ter pelo menos 10 caracteres"),
  compensation: z.string().min(1, "Remuneração é obrigatória"),
  jobLevel: z.string().min(1, "Nível é obrigatório"),
  department: z.string().min(1, "Departamento é obrigatório"),
  hiringManagerId: z.string().min(1, "Hiring Manager é obrigatório"),
  salaryExpected: z.string().min(1, "Salário é obrigatório"),
});

type EditJobFormValues = z.infer<typeof editJobFormSchema>;

export default function JobDetail() {
  const [, params] = useRoute("/jobs/:id");
  const jobId = params?.id;
  const { toast } = useToast();
  const { isSuperAdmin, isExternal } = useAuth();
  const [_confirmingNotifiedId, _setConfirmingNotifiedId] = useState<string | null>(null);
  const _confirmingNotifiedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [hiddenColumnIds, setHiddenColumnIds] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem(`kanban-hidden-columns-${jobId}`);
      if (stored) return new Set(JSON.parse(stored));
    } catch {}
    return new Set();
  });

  useEffect(() => {
    if (!jobId) return;
    if (hiddenColumnIds.size === 0) {
      localStorage.removeItem(`kanban-hidden-columns-${jobId}`);
    } else {
      localStorage.setItem(
        `kanban-hidden-columns-${jobId}`,
        JSON.stringify(Array.from(hiddenColumnIds)),
      );
    }
  }, [hiddenColumnIds, jobId]);
  const { getParam, setParam, removeParam } = useQueryParams();
  const settingsTab = getParam("settings-tab") as
    | "edit-job"
    | "edit-stages"
    | "settings"
    | "offer-approval"
    | null;
  const isSettingsDialogOpen = !!settingsTab;
  const selectedApplicationId = getParam("applicationId");
  const isDetailModalOpen = !!selectedApplicationId;
  const searchQuery = getParam("search") || "";
  const activeTab = getParam("tab") || "kanban";

  const setSelectedApplicationId = useCallback(
    (id: string | null) => {
      if (id) {
        setParam("applicationId", id);
      } else {
        removeParam("applicationId");
      }
    },
    [setParam, removeParam],
  );

  const setIsDetailModalOpen = useCallback(
    (open: boolean) => {
      if (!open) {
        removeParam("applicationId");
      }
    },
    [removeParam],
  );

  const setActiveTab = useCallback(
    (tab: string) => {
      setParam("tab", tab);
    },
    [setParam],
  );

  const setSearchQuery = useCallback(
    (query: string) => {
      if (query) {
        setParam("search", query);
      } else {
        removeParam("search");
      }
    },
    [setParam, removeParam],
  );

  const [isAddApplicationOpen, setIsAddApplicationOpen] = useState(false);
  const [propertyFieldValues, setPropertyFieldValues] = useState<Record<string, string | string[]>>(
    {},
  );
  const [isCloseJobOpen, setIsCloseJobOpen] = useState(false);
  const [deletingStageId, setDeletingStageId] = useState<string | null>(null);
  const [isNotionLinkOpen, setIsNotionLinkOpen] = useState(false);
  const [isNotionSyncOpen, setIsNotionSyncOpen] = useState(false);
  const [isExternalDbImportOpen, setIsExternalDbImportOpen] = useState(false);
  const [isPropertyConfigOpen, setIsPropertyConfigOpen] = useState(false);
  const [activeAnalyticsViewId, setActiveAnalyticsViewId] = useState<string | null>(() => {
    const tab = getParam("tab");
    return tab?.startsWith("analytics-") ? tab.replace("analytics-", "") : null;
  });
  const [editorState, setEditorState] = useState<
    { mode: "create" } | { mode: "edit"; view: AnalyticsView } | null
  >(null);
  const [editStageInterviewerSearch, setEditStageInterviewerSearch] = useState<
    Record<number, string>
  >({});
  const [editableStages, setEditableStages] = useState<
    Array<{
      id?: string;
      stageName: string;
      stageOrder: number;
      durationMinutes: number | "";
      defaultInterviewerId: string | null;
      interviewerIds: string[];
      interviewerGroups: { id?: string; employeeIds: string[]; schedulingLink: string | null }[];
      hidden: boolean;
      materials: string | null;
      description: string | null;
      feedbackFormId: string | null;
    }>
  >([]);
  const [expandedStages, setExpandedStages] = useState<Record<number, boolean>>({});
  const [archiveDialogStage, setArchiveDialogStage] = useState<{ id: string; name: string } | null>(
    null,
  );
  const [archiveAction, setArchiveAction] = useState<"keep" | "migrate">("keep");
  const [archiveTargetStageId, setArchiveTargetStageId] = useState<string>("");
  const [showArchivedOnBoard, setShowArchivedOnBoard] = useState(false);

  const form = useForm<ApplicationFormValues>({
    resolver: zodResolver(applicationFormSchema),
    defaultValues: {
      applicantName: "",
      applicantEmail: "",
      applicantPhone: "",
      linkedinUrl: "",
      type: "referral",
    },
  });

  const editJobForm = useForm<EditJobFormValues>({
    resolver: zodResolver(editJobFormSchema),
    defaultValues: {
      title: "",
      generalResponsibilities: "",
      desiredProfile: "",
      compensation: "",
      jobLevel: "",
      department: "",
      hiringManagerId: "",
      salaryExpected: "",
    },
  });

  const { data: kanban, isLoading } = useQuery(
    orpc.applications.getJobKanban.queryOptions({
      input: { jobOpeningId: jobId! },
      enabled: !!jobId,
    }),
  ) as { data: KanbanResponse | undefined; isLoading: boolean };

  // Unified ATS management capability (admin OR recruiting OR hiring manager),
  // computed by the backend. Gates both the drag (kanban) and modal move paths.
  const canManage = kanban?.canManage === true;

  const { data: people } = useQuery(orpc.dashboard.people.queryOptions()) as {
    data: PersonOption[] | undefined;
  };

  const peopleById = useMemo(() => {
    const map: Record<string, { id: string; fullName: string }> = {};
    for (const p of people ?? []) map[p.id] = { id: p.id, fullName: p.fullName };
    return map;
  }, [people]);

  const { data: stageInterviewers, isLoading: isLoadingStageInterviewers } = useQuery(
    orpc.jobOpenings.getStageInterviewers.queryOptions({
      input: { id: jobId! },
      enabled: !!jobId,
    }),
  ) as { data: StageInterviewersData | undefined; isLoading: boolean };

  const { data: analyticsViews = [] } = useQuery(
    orpc.jobOpenings.listAnalyticsViews.queryOptions({
      input: { id: jobId! },
      enabled: !!jobId,
    }),
  ) as { data: AnalyticsView[] };

  const moveApp = useMoveApplication({
    jobId,
    jobTitle: kanban?.title || "",
    jobLevel: kanban?.jobLevel,
    jobDepartment: kanban?.department,
    hiringManagerId: kanban?.hiringManager?.id,
    hiringManagerFullName: kanban?.hiringManager?.fullName,
    interviewStages: kanban?.interviewStages,
    stageInterviewers,
    isLoadingStageInterviewers,
    people,
    canManage,
  });

  const kanbanQueryKey = orpc.applications.getJobKanban.queryOptions({
    input: { jobOpeningId: jobId! },
  }).queryKey as unknown as unknown[];

  const addApplicationMutation = useMutation({
    mutationFn: async (data: ApplicationFormValues) => {
      return rpcClient.applications.create({
        ...data,
        jobOpeningId: jobId!,
      });
    },
    onSuccess: async (createdApplication) => {
      const propertyDefs = kanban?.propertyDefinitions ?? [];
      const filledProps = propertyDefs.filter((def) => {
        const val = propertyFieldValues[def.id];
        return val !== undefined && val !== "" && !(Array.isArray(val) && val.length === 0);
      });

      if (filledProps.length > 0) {
        await Promise.all(
          filledProps.map((def) =>
            rpcClient.applications.upsertPropertyValue({
              id: createdApplication.id,
              defId: def.id,
              value: propertyFieldValues[def.id],
            }),
          ),
        );
      }

      setPropertyFieldValues({});
      queryClient.invalidateQueries({ queryKey: kanbanQueryKey });
      setIsAddApplicationOpen(false);
      form.reset();
      toast({
        title: "Candidato adicionado",
        description: "A aplicação foi criada com sucesso.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Erro",
        description: error.message || "Não foi possível adicionar o candidato.",
        variant: "destructive",
      });
    },
  });

  const jobListQueryKey = orpc.jobOpenings.list.queryOptions().queryKey;

  const closeJobMutation = useMutation({
    mutationFn: async () => {
      return rpcClient.jobOpenings.close({ id: jobId! });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kanbanQueryKey });
      queryClient.invalidateQueries({ queryKey: jobListQueryKey });
      setIsCloseJobOpen(false);
      toast({
        title: "Vaga fechada",
        description: "A vaga foi fechada com sucesso.",
      });
    },
    onError: () => {
      toast({
        title: "Erro",
        description: "Não foi possível fechar a vaga.",
        variant: "destructive",
      });
    },
  });

  const toggleHiddenMutation = useMutation({
    mutationFn: async () => {
      return rpcClient.jobOpenings.toggleHiddenFromCareers({ id: jobId! });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kanbanQueryKey });
      queryClient.invalidateQueries({ queryKey: jobListQueryKey });
      toast({
        title: kanban?.hiddenFromCareers ? "Vaga visível" : "Vaga oculta",
        description: kanban?.hiddenFromCareers
          ? "A vaga agora está visível na página de carreiras."
          : "A vaga foi ocultada da página de carreiras.",
      });
    },
    onError: () => {
      toast({
        title: "Erro",
        description: "Não foi possível alterar a visibilidade da vaga.",
        variant: "destructive",
      });
    },
  });

  const toggleAutoAdvanceMutation = useMutation({
    mutationFn: async () => {
      return rpcClient.jobOpenings.toggleAutoAdvance({ id: jobId! });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kanbanQueryKey });
      queryClient.invalidateQueries({ queryKey: jobListQueryKey });
      toast({
        title: kanban?.allowAutoAdvance
          ? "Avanço automático desabilitado"
          : "Avanço automático habilitado",
        description: kanban?.allowAutoAdvance
          ? "Candidatos não serão mais avançados automaticamente após aprovação."
          : "Candidatos serão avançados automaticamente após aprovação.",
      });
    },
    onError: () => {
      toast({
        title: "Erro",
        description: "Não foi possível alterar a configuração de avanço automático.",
        variant: "destructive",
      });
    },
  });

  const toggleNotifyHmFeedbackMutation = useMutation({
    mutationFn: async () => {
      return rpcClient.jobOpenings.toggleNotifyHmOnFeedback({ id: jobId! });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kanbanQueryKey });
      queryClient.invalidateQueries({ queryKey: jobListQueryKey });
      toast({
        title: kanban?.notifyHmOnFeedback
          ? "Notificação de avaliação desabilitada"
          : "Notificação de avaliação habilitada",
        description: kanban?.notifyHmOnFeedback
          ? "Você não receberá mais notificações Slack quando um entrevistador enviar uma avaliação."
          : "Você receberá notificações Slack quando um entrevistador enviar uma avaliação.",
      });
    },
    onError: () => {
      toast({
        title: "Erro",
        description: "Não foi possível alterar a configuração de notificação.",
        variant: "destructive",
      });
    },
  });

  const _markNotifiedMutation = useMutation({
    mutationFn: async (interviewId: string) => {
      return rpcClient.interviews.markNotified({ id: interviewId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kanbanQueryKey });
      toast({
        title: "Candidato marcado como comunicado",
        description: "O status de comunicação foi atualizado.",
      });
    },
    onError: () => {
      toast({
        title: "Erro",
        description: "Não foi possível marcar como comunicado.",
        variant: "destructive",
      });
    },
  });

  const { data: digestSchedule = [], refetch: refetchDigestSchedule } = useQuery(
    orpc.jobOpenings.getDigestSchedule.queryOptions({
      input: { id: jobId! },
      enabled: !!kanban?.notifyHmDailyDigest,
    }),
  );

  const addDigestHourMutation = useMutation({
    mutationFn: async (hour: number) => {
      await rpcClient.jobOpenings.addDigestScheduleHour({ id: jobId!, hour });
    },
    onSuccess: () => {
      refetchDigestSchedule();
    },
    onError: () =>
      toast({
        title: "Erro",
        description: "Não foi possível adicionar horário.",
        variant: "destructive",
      }),
  });

  const deleteDigestEntryMutation = useMutation({
    mutationFn: async (scheduleId: string) => {
      await rpcClient.jobOpenings.deleteDigestScheduleEntry({ id: jobId!, scheduleId });
    },
    onSuccess: () => {
      refetchDigestSchedule();
    },
    onError: () =>
      toast({
        title: "Erro",
        description: "Não foi possível remover horário.",
        variant: "destructive",
      }),
  });

  const applyDigestToAllMutation = useMutation({
    mutationFn: async () => {
      await rpcClient.jobOpenings.applyDigestScheduleToAll({ id: jobId! });
    },
    onSuccess: () =>
      toast({ title: "Aplicado", description: "Horários aplicados a todas as suas vagas." }),
    onError: () =>
      toast({
        title: "Erro",
        description: "Não foi possível aplicar a todas as vagas.",
        variant: "destructive",
      }),
  });

  const toggleNotifyHmDigestMutation = useMutation({
    mutationFn: async () => {
      return rpcClient.jobOpenings.toggleNotifyHmDailyDigest({ id: jobId! });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kanbanQueryKey });
      queryClient.invalidateQueries({ queryKey: jobListQueryKey });
      refetchDigestSchedule();
      toast({
        title: kanban?.notifyHmDailyDigest
          ? "Digest diário desabilitado"
          : "Digest diário habilitado",
        description: kanban?.notifyHmDailyDigest
          ? "Você não receberá mais o resumo diário de candidatos pendentes."
          : "Você receberá um resumo diário de candidatos que precisam de atenção.",
      });
    },
    onError: () => {
      toast({
        title: "Erro",
        description: "Não foi possível alterar a configuração de digest.",
        variant: "destructive",
      });
    },
  });

  const toggleNotifyHmNewApplicationMutation = useMutation({
    mutationFn: async () => {
      return rpcClient.jobOpenings.toggleNotifyHmOnNewApplication({ id: jobId! });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kanbanQueryKey });
      queryClient.invalidateQueries({ queryKey: jobListQueryKey });
      toast({
        title: kanban?.notifyHmOnNewApplication
          ? "Alerta de nova candidatura desabilitado"
          : "Alerta de nova candidatura habilitado",
        description: kanban?.notifyHmOnNewApplication
          ? "Você não receberá mais notificações quando novos candidatos se inscreverem."
          : "Você receberá notificações Slack quando novos candidatos se inscreverem.",
      });
    },
    onError: () => {
      toast({
        title: "Erro",
        description: "Não foi possível alterar a configuração de notificação.",
        variant: "destructive",
      });
    },
  });

  const updateOfferApprovalRulesMutation = useMutation({
    mutationFn: async (rules: OfferApprovalRules | null) => {
      return rpcClient.jobOpenings.updateOfferApprovalRules({
        id: jobId!,
        offerApprovalRules: rules,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kanbanQueryKey });
      queryClient.invalidateQueries({ queryKey: jobListQueryKey });
      toast({
        title: "Regras de aprovação salvas",
        description: "As regras de aprovação de carta oferta foram atualizadas.",
      });
    },
    onError: () => {
      toast({
        title: "Erro",
        description: "Não foi possível salvar as regras de aprovação.",
        variant: "destructive",
      });
    },
  });

  const stageInterviewersQueryKey = orpc.jobOpenings.getStageInterviewers.queryOptions({
    input: { id: jobId! },
  }).queryKey;

  const updateStagesMutation = useMutation({
    mutationFn: async (stages: typeof editableStages) => {
      return rpcClient.jobOpenings.updateInterviewStages({
        id: jobId!,
        stages: stages.map((s, idx) => ({
          ...(s.id ? { id: s.id } : {}),
          stageName: s.stageName,
          stageOrder: idx + 1,
          durationMinutes: s.durationMinutes || 30,
          defaultInterviewerId: s.defaultInterviewerId || undefined,
          interviewerIds: s.interviewerIds,
          interviewerGroups: s.interviewerGroups.filter((g) => g.employeeIds.length >= 2),
          hidden: s.hidden,
          materials: s.materials ?? null,
          description: s.description ?? null,
          feedbackFormId: s.feedbackFormId ?? null,
        })),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kanbanQueryKey });
      queryClient.invalidateQueries({ queryKey: stageInterviewersQueryKey });
      removeParam("settings-tab");
      toast({ title: "Etapas atualizadas", description: "As etapas de entrevista foram salvas." });
    },
    onError: () => {
      toast({
        title: "Erro",
        description: "Não foi possível salvar as etapas.",
        variant: "destructive",
      });
    },
  });

  const updateJobMutation = useMutation({
    mutationFn: async (data: EditJobFormValues) => {
      return rpcClient.jobOpenings.update({ id: jobId!, ...data });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kanbanQueryKey });
      queryClient.invalidateQueries({ queryKey: jobListQueryKey });
      removeParam("settings-tab");
      toast({ title: "Vaga atualizada", description: "As informações da vaga foram salvas." });
    },
    onError: () => {
      toast({
        title: "Erro",
        description: "Não foi possível salvar as informações.",
        variant: "destructive",
      });
    },
  });

  const _deleteStageMutation = useMutation({
    mutationFn: async (stageId: string) => {
      setDeletingStageId(stageId);
      return rpcClient.jobOpenings.deleteInterviewStage({ id: jobId!, stageId });
    },
    onSuccess: (_data, stageId) => {
      setEditableStages((prev) => prev.filter((s) => s.id !== stageId));
      setDeletingStageId(null);
      queryClient.invalidateQueries({ queryKey: kanbanQueryKey });
      queryClient.invalidateQueries({ queryKey: stageInterviewersQueryKey });
      toast({ title: "Etapa removida", description: "A etapa foi deletada com sucesso." });
    },
    onError: async (error: Error) => {
      setDeletingStageId(null);
      toast({
        title: "Erro",
        description: error.message || "Não foi possível deletar a etapa.",
        variant: "destructive",
      });
    },
  });

  const archiveInfoMutation = useMutation({
    mutationFn: async (stageId: string) => {
      return rpcClient.jobOpenings.getStageArchiveInfo({ id: jobId!, stageId });
    },
  });

  const archiveStageMutation = useMutation({
    mutationFn: async ({
      stageId,
      action,
      targetStageId,
    }: {
      stageId: string;
      action: "keep" | "migrate";
      targetStageId?: string;
    }) => {
      return rpcClient.jobOpenings.archiveInterviewStage({
        id: jobId!,
        stageId,
        action,
        targetStageId,
      });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: kanbanQueryKey });
      queryClient.invalidateQueries({ queryKey: stageInterviewersQueryKey });
      setEditableStages((prev) => prev.filter((s) => s.id !== variables.stageId));
      setArchiveDialogStage(null);
      setArchiveAction("keep");
      setArchiveTargetStageId("");
      toast({ title: "Etapa arquivada", description: "A etapa foi arquivada com sucesso." });
    },
    onError: async (error: Error) => {
      const message = error.message || "Erro ao arquivar etapa";
      toast({ title: "Erro", description: message, variant: "destructive" });
    },
  });

  const unarchiveStageMutation = useMutation({
    mutationFn: async (stageId: string) => {
      return rpcClient.jobOpenings.unarchiveInterviewStage({ id: jobId!, stageId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kanbanQueryKey });
      queryClient.invalidateQueries({ queryKey: stageInterviewersQueryKey });
      toast({ title: "Etapa restaurada", description: "A etapa foi desarquivada." });
    },
    onError: (error: Error) => {
      toast({
        title: "Erro",
        description: error.message || "Erro ao desarquivar etapa",
        variant: "destructive",
      });
    },
  });

  const analyticsViewsQueryKey = orpc.jobOpenings.listAnalyticsViews.queryOptions({
    input: { id: jobId! },
  }).queryKey;

  const createViewMutation = useMutation({
    mutationFn: async () => {
      return rpcClient.jobOpenings.createAnalyticsView({
        id: jobId!,
        name: "Nova view",
        chartType: "bar",
        primaryDimension: { type: "stages" },
        secondaryDimension: null,
      });
    },
    onSuccess: (newView) => {
      queryClient.invalidateQueries({ queryKey: analyticsViewsQueryKey });
      setActiveAnalyticsViewId(newView.id);
      setActiveTab(`analytics-${newView.id}`);
      setEditorState({ mode: "edit", view: newView });
    },
    onError: () => {
      toast({ title: "Erro ao criar view", variant: "destructive" });
    },
  });

  const deleteViewMutation = useMutation({
    mutationFn: (viewId: string) =>
      rpcClient.jobOpenings.deleteAnalyticsView({ id: jobId!, viewId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: analyticsViewsQueryKey });
      setActiveAnalyticsViewId(null);
      setEditorState(null);
      setActiveTab("kanban");
    },
    onError: () => {
      toast({ title: "Erro ao excluir view", variant: "destructive" });
    },
  });

  const openSettingsDialog = (
    tab: "edit-job" | "edit-stages" | "feedback-forms" | "settings" | "offer-approval" = "settings",
  ) => {
    if (!kanban) return;
    // Always reset edit-job form with current values
    editJobForm.reset({
      title: kanban.title,
      generalResponsibilities: kanban.generalResponsibilities,
      desiredProfile: kanban.desiredProfile,
      compensation: kanban.compensation,
      jobLevel: kanban.jobLevel,
      department: kanban.department,
      hiringManagerId: kanban.hiringManager?.id || "",
      salaryExpected: kanban.salaryExpected,
    });
    // Always reset edit-stages with current values
    setEditableStages(
      kanban.interviewStages
        .filter((s) => !s.archivedAt)
        .map((s) => {
          const stageInterviewerData = stageInterviewers?.find((si) => si.stageId === s.id);
          return {
            id: s.id,
            stageName: s.stageName,
            stageOrder: s.stageOrder,
            durationMinutes: s.durationMinutes,
            defaultInterviewerId: s.defaultInterviewerId,
            interviewerIds: stageInterviewerData?.employees.map((e) => e.id) || [],
            interviewerGroups:
              stageInterviewerData?.groups?.map((g) => ({
                id: g.id,
                employeeIds: g.members.map((m) => m.id),
                schedulingLink: g.schedulingLink,
              })) || [],
            hidden: s.hidden,
            materials: s.materials ?? null,
            description: s.description ?? null,
            feedbackFormId: s.feedbackFormId ?? null,
          };
        }),
    );
    setEditStageInterviewerSearch({});
    setExpandedStages({});
    setParam("settings-tab", tab);
  };

  const columns: KanbanColumn[] = kanban
    ? [
        { id: "applied", title: STATUS_LABELS.applied, type: "fixed" },
        { id: "prioritized", title: STATUS_LABELS.prioritized, type: "fixed" },
        { id: "deprioritized", title: STATUS_LABELS.deprioritized, type: "fixed" },
        ...kanban.interviewStages
          .filter((stage) => !stage.hidden && (showArchivedOnBoard || !stage.archivedAt))
          .map((stage) => ({
            id: stage.id,
            title: stage.stageName,
            type: "stage" as const,
            stageId: stage.id,
            archived: !!stage.archivedAt,
          })),
        { id: "reference_checks", title: STATUS_LABELS.reference_checks, type: "fixed" },
        { id: "dossier", title: STATUS_LABELS.dossier, type: "fixed" },
        { id: "offer", title: STATUS_LABELS.offer, type: "fixed" },
        { id: "hired", title: STATUS_LABELS.hired, type: "fixed" },
        { id: "rejected", title: STATUS_LABELS.rejected, type: "fixed" },
        { id: "offer_rejected", title: STATUS_LABELS.offer_rejected, type: "fixed" },
      ]
    : [];

  const filteredStages = useMemo(() => {
    if (!kanban || !searchQuery) return kanban?.stages || {};
    const result: Record<string, ApplicationWithDetails[]> = {};
    for (const [key, apps] of Object.entries(kanban.stages)) {
      result[key] = (apps as ApplicationWithDetails[]).filter(
        (app) =>
          matchesSearch(app.applicant?.fullName || "", searchQuery) ||
          matchesSearch(app.applicant?.email || "", searchQuery),
      );
    }
    return result;
  }, [kanban, searchQuery]);

  const getColumnApplications = (column: KanbanColumn): ApplicationWithDetails[] => {
    if (!kanban) return [];
    return filteredStages[column.id] || [];
  };

  const kanbanScrollRef = useRef<HTMLDivElement>(null);

  const handleMoveCard = useCallback(
    async ({
      applicationId,
      targetColumn: column,
      application,
    }: {
      applicationId: string;
      targetColumn: KanbanColumn;
      application: KanbanApplication;
    }) => {
      if (!canManage || !kanban) return;

      const targetColumn = column.type === "stage" ? `stage-${column.stageId}` : column.id;
      const targetInterviewStageId = column.type === "stage" ? (column.stageId ?? null) : null;

      const currentColumnId = Object.entries(kanban.stages).find(([, apps]) =>
        (apps as { id: string }[]).some((a) => a.id === applicationId),
      )?.[0];
      if (currentColumnId === targetColumn) return;

      const applicantName = application.applicant?.fullName || "Candidato";
      const isInterviewStage = column.type === "stage";
      const stage = isInterviewStage
        ? kanban.interviewStages.find((s) => s.id === column.stageId)
        : undefined;

      await moveApp.initMove({
        applicationId,
        targetColumn,
        targetInterviewStageId,
        applicantName,
        targetColumnTitle: column.title,
        isInterviewStage,
        stage,
      });
    },
    [canManage, kanban, moveApp],
  );

  const handleKanbanCardSelect = useCallback(
    (applicationId: string) => {
      setSelectedApplicationId(applicationId);
      setIsDetailModalOpen(true);
    },
    [setSelectedApplicationId, setIsDetailModalOpen],
  );

  const handleKanbanCardDelete = useCallback(
    (applicationId: string) => {
      if (!canManage || !kanban) return;
      const allApplications = Object.values(kanban.stages).flat();
      const application = allApplications.find((app) => app.id === applicationId);
      if (!application) return;
      const applicantName = application.applicant?.fullName || "Candidato";
      moveApp.initMove({
        applicationId,
        targetColumn: "rejected",
        targetInterviewStageId: null,
        applicantName,
        targetColumnTitle: STATUS_LABELS.rejected,
        isInterviewStage: false,
        stage: undefined,
      });
    },
    [canManage, kanban, moveApp],
  );

  const { keyboardSelectedId } = useKanbanKeyboardNav({
    columns,
    getColumnApplications,
    hiddenColumnIds,
    searchQuery,
    isModalOpen: isDetailModalOpen || moveApp.isConfirmOpen,
    onSelect: handleKanbanCardSelect,
    onDelete: handleKanbanCardDelete,
    kanbanScrollRef,
    activeTab,
  });

  const totalApplications = kanban
    ? Object.values(kanban.stages).reduce((sum, apps) => sum + apps.length, 0)
    : 0;

  if (isLoading) {
    return (
      <div className="space-y-2 p-6">
        {["a", "b", "c", "d", "e"].map((k) => (
          <div key={k} className="flex items-center gap-3 py-2">
            <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--sand-divider))]" />
            <span className="h-4 flex-1 max-w-md rounded bg-[hsl(var(--sand-divider))]" />
          </div>
        ))}
      </div>
    );
  }

  if (!kanban) {
    return (
      <div>
        <p>Vaga não encontrada.</p>
        <Link href="/jobs">
          <Button variant="outline" className="mt-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
        </Link>
      </div>
    );
  }

  const onSubmitApplication = (data: ApplicationFormValues) => {
    addApplicationMutation.mutate(data);
  };

  return (
    <div className="flex flex-col flex-1 min-h-0 gap-6 overflow-hidden">
      <div className="shrink-0 space-y-3">
        <PageHeader
          title={kanban.title}
          description={`${getDepartmentLabel(kanban.department)} · ${getJobLevelLabel(kanban.jobLevel)}`}
          backTo="/jobs"
          titleExtra={<StatusBadge status={kanban.status} />}
        >
          {kanban.status === "open" && (
            <Button
              onClick={() => setIsAddApplicationOpen(true)}
              size="sm"
              data-testid="button-add-application"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Adicionar Candidato
            </Button>
          )}
          {!isExternal && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  data-testid="button-more-actions"
                >
                  <MoreHorizontal className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {!isExternal && (
                  <DropdownMenuItem
                    onClick={() => openSettingsDialog("edit-job")}
                    data-testid="menu-job-settings"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Configurações
                  </DropdownMenuItem>
                )}
                {!isExternal && (
                  <DropdownMenuItem
                    onClick={() => setIsPropertyConfigOpen(true)}
                    data-testid="menu-property-config"
                  >
                    <SlidersHorizontal className="mr-2 h-4 w-4" />
                    Propriedades
                  </DropdownMenuItem>
                )}
                {kanban.status === "open" && canManage && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => setIsCloseJobOpen(true)}
                      data-testid="menu-close-job"
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Fechar Vaga
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-xs text-muted-foreground">
                  Integrações
                </DropdownMenuLabel>
                {kanban.notionId ? (
                  <DropdownMenuItem
                    onClick={() => setIsNotionSyncOpen(true)}
                    data-testid="menu-sync-notion"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Sincronizar com Notion
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem
                    onClick={() => setIsNotionLinkOpen(true)}
                    data-testid="menu-link-notion"
                  >
                    <Link2 className="w-4 h-4 mr-2" />
                    Vincular ao Notion
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onClick={() => setIsExternalDbImportOpen(true)}
                  data-testid="menu-import-external-db"
                >
                  <Database className="w-4 h-4 mr-2" />
                  Importar de outra database
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </PageHeader>
        <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap pl-11">
          <span className="flex items-center gap-1.5" data-testid="text-salary">
            <DollarSign className="w-3.5 h-3.5" />
            {formatCurrency(kanban.salaryExpected)}
          </span>
          <span className="flex items-center gap-1.5" data-testid="text-hiring-manager">
            <UserCog className="w-3.5 h-3.5" />
            {kanban.hiringManager?.fullName || "—"}
          </span>
          <span className="flex items-center gap-1.5" data-testid="text-total-candidates">
            <Users className="w-3.5 h-3.5" />
            {totalApplications} candidato{totalApplications !== 1 ? "s" : ""}
          </span>
          <span className="flex items-center gap-1.5" data-testid="text-total-stages">
            <Briefcase className="w-3.5 h-3.5" />
            {kanban.interviewStages.filter((s) => !s.hidden).length} etapa
            {kanban.interviewStages.filter((s) => !s.hidden).length !== 1 ? "s" : ""}
          </span>
        </div>
        {!isExternal && (
          <>
            <LinkToNotionButton
              jobOpeningId={kanban.jobOpeningId}
              externalOpen={isNotionLinkOpen}
              onExternalOpenChange={setIsNotionLinkOpen}
            />
            <SyncNotionCandidatesButton
              jobOpeningId={kanban.jobOpeningId}
              externalOpen={isNotionSyncOpen}
              onExternalOpenChange={setIsNotionSyncOpen}
            />
            <ImportFromExternalDatabaseButton
              jobOpeningId={kanban.jobOpeningId}
              externalOpen={isExternalDbImportOpen}
              onExternalOpenChange={setIsExternalDbImportOpen}
            />
          </>
        )}
      </div>

      <div className="flex items-center justify-between gap-4 shrink-0">
        <Tabs
          value={activeTab}
          onValueChange={(val) => {
            setActiveTab(val);
            setEditorState(null);
            if (val.startsWith("analytics-")) {
              setActiveAnalyticsViewId(val.replace("analytics-", ""));
            }
          }}
          className="w-auto"
        >
          <TabsList className="justify-start w-auto">
            <TabsTrigger value="kanban" data-testid="tab-kanban">
              Kanban
            </TabsTrigger>
            <TabsTrigger value="candidates" data-testid="tab-candidates">
              Candidatos
            </TabsTrigger>

            {analyticsViews.length > 0 && <div className="w-px h-5 bg-border mx-1 self-center" />}

            {analyticsViews.map((view) => (
              <TabsTrigger key={view.id} value={`analytics-${view.id}`}>
                {view.name}
              </TabsTrigger>
            ))}

            <button
              type="button"
              onClick={() => createViewMutation.mutate()}
              disabled={createViewMutation.isPending}
              className="flex items-center gap-1 px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground disabled:opacity-50"
            >
              <Plus className="h-3.5 w-3.5" />
              {createViewMutation.isPending ? "Criando..." : "Adicionar view"}
            </button>
          </TabsList>
        </Tabs>
        {activeTab === "kanban" && (
          <div className="flex items-center gap-2">
            <div className="relative max-w-sm w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-9 focus-visible:ring-0 focus-visible:ring-offset-0"
                data-testid="input-search-kanban"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  data-testid="button-clear-search"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            {kanban?.interviewStages.some((s) => s.archivedAt) && (
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant={showArchivedOnBoard ? "secondary" : "ghost"}
                      size="icon"
                      className="h-9 w-9 flex-shrink-0"
                      onClick={() => setShowArchivedOnBoard(!showArchivedOnBoard)}
                    >
                      <Archive className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {showArchivedOnBoard
                      ? "Ocultar etapas arquivadas"
                      : "Mostrar etapas arquivadas"}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        )}
      </div>

      {activeTab === "kanban" && !editorState && (
        <JobPipelineKanbanView
          data={{
            columns,
            stagesByColumn: filteredStages as Record<string, KanbanApplication[]>,
            interviewStages: kanban?.interviewStages ?? [],
            propertyDefinitions: kanban?.propertyDefinitions ?? [],
            stageInterviewers,
            peopleById,
            notifyHmDailyDigest: kanban?.notifyHmDailyDigest ?? false,
          }}
          actions={{
            onMoveCard: handleMoveCard,
            onSelectCard: handleKanbanCardSelect,
            onHideColumn: (id) =>
              setHiddenColumnIds((prev) => {
                const next = new Set(prev);
                next.add(id);
                return next;
              }),
            onShowColumn: (id) =>
              setHiddenColumnIds((prev) => {
                const next = new Set(prev);
                next.delete(id);
                return next;
              }),
            onDeleteCard: handleKanbanCardDelete,
          }}
          hiddenColumnIds={hiddenColumnIds}
          searchQuery={searchQuery}
          keyboardSelectedId={keyboardSelectedId}
          canManage={canManage}
          scrollRef={kanbanScrollRef}
          renderPropertyValue={({ definitionId, applicationId }) => {
            const def = kanban?.propertyDefinitions.find((d) => d.id === definitionId);
            const apps = Object.values(kanban?.stages ?? {}).flat() as Array<{
              id: string;
              propertyValues?: PropertyValue[];
            }>;
            const app = apps.find((a) => a.id === applicationId);
            const val = app?.propertyValues?.find((pv) => pv.definitionId === definitionId);
            if (!def) return null;
            return (
              <PropertyValueCell
                key={def.id}
                definition={def}
                value={val}
                applicationId={applicationId}
                jobQueryKey={kanbanQueryKey}
              />
            );
          }}
        />
      )}

      {activeTab === "candidates" && !editorState && (
        <div className="flex-1 min-h-0 overflow-auto pb-6">
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-4">
                {Object.values(kanban.stages)
                  .flat()
                  .map((app) => (
                    // biome-ignore lint/a11y/noStaticElementInteractions: role/tabIndex are conditional on isExternal
                    <div
                      key={app.id}
                      className={`flex items-center justify-between p-4 border rounded-lg ${isExternal ? "" : "hover-elevate cursor-pointer"}`}
                      role={isExternal ? undefined : "button"}
                      tabIndex={isExternal ? undefined : 0}
                      onClick={
                        isExternal
                          ? undefined
                          : () => {
                              setSelectedApplicationId(app.id);
                              setIsDetailModalOpen(true);
                            }
                      }
                      onKeyDown={
                        isExternal
                          ? undefined
                          : (e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                setSelectedApplicationId(app.id);
                                setIsDetailModalOpen(true);
                              }
                            }
                      }
                      data-testid={`candidate-row-${app.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <User className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium">{app.applicant?.fullName || "Candidato"}</p>
                          <p className="text-sm text-muted-foreground">
                            {app.applicant?.email || "-"}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <StatusBadge status={app.status} />
                        {app.aiVettingScore !== null && app.aiVettingScore !== undefined && (
                          <div className="flex items-center gap-1 text-sm">
                            <Brain className="w-4 h-4 text-muted-foreground" />
                            <span>{app.aiVettingScore}%</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                {Object.values(kanban.stages).flat().length === 0 && (
                  <p className="text-center text-muted-foreground py-8">
                    Nenhum candidato nesta vaga
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Analytics view editor */}
      {editorState && (
        <AnalyticsViewEditor
          jobOpeningId={jobId!}
          propertyDefs={kanban?.propertyDefinitions ?? []}
          view={editorState.mode === "edit" ? editorState.view : undefined}
          onDone={() => {
            setEditorState(null);
            queryClient.invalidateQueries({ queryKey: analyticsViewsQueryKey });
          }}
          onCancel={() => {
            setEditorState(null);
            setActiveTab(activeAnalyticsViewId ? `analytics-${activeAnalyticsViewId}` : "kanban");
          }}
        />
      )}

      {/* Analytics chart view */}
      {!editorState &&
        activeTab.startsWith("analytics-") &&
        activeAnalyticsViewId &&
        (() => {
          const view = analyticsViews.find((v) => v.id === activeAnalyticsViewId);
          if (!view) return null;
          return (
            <JobAnalyticsTab
              view={view}
              jobOpeningId={jobId!}
              onEdit={() => setEditorState({ mode: "edit", view })}
              onDelete={() => deleteViewMutation.mutate(view.id)}
            />
          );
        })()}

      <MoveConfirmationDialog
        moveState={moveApp}
        people={people}
        stageInterviewers={stageInterviewers}
        isLoadingStageInterviewers={isLoadingStageInterviewers}
        jobId={jobId}
        jobTitle={kanban?.title || ""}
        jobLevel={kanban?.jobLevel}
      />

      <ApplicantDetailModal
        applicationId={selectedApplicationId}
        open={isDetailModalOpen}
        onOpenChange={setIsDetailModalOpen}
        propertyDefinitions={kanban?.propertyDefinitions ?? []}
        propertyValues={
          Object.values(kanban?.stages ?? {})
            .flat()
            .find((a) => a.id === selectedApplicationId)?.propertyValues ?? []
        }
      />

      <Dialog
        open={isAddApplicationOpen}
        onOpenChange={(open) => {
          setIsAddApplicationOpen(open);
          if (!open) setPropertyFieldValues({});
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Adicionar Candidato</DialogTitle>
            <DialogDescription>Adicione um novo candidato à vaga {kanban.title}</DialogDescription>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmitApplication)} className="space-y-4">
              <FormField
                control={form.control}
                name="applicantName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nome completo</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="João Silva"
                        {...field}
                        data-testid="input-applicant-name"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="applicantEmail"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email (opcional)</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="joao@email.com"
                        {...field}
                        data-testid="input-applicant-email"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="applicantPhone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Telefone (opcional)</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="+55 11 99999-9999"
                        {...field}
                        data-testid="input-applicant-phone"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="linkedinUrl"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>LinkedIn (opcional)</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="https://linkedin.com/in/joao"
                        {...field}
                        data-testid="input-linkedin-url"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Origem</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger data-testid="select-application-type">
                          <SelectValue placeholder="Selecione a origem" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {APPLICATION_TYPES.map((t) => (
                          <SelectItem key={t.value} value={t.value}>
                            {t.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {/* Optional property fields */}
              {(kanban?.propertyDefinitions ?? []).length > 0 && (
                <>
                  <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mt-2 mb-1">
                    Propriedades (opcional)
                  </div>
                  {(kanban?.propertyDefinitions ?? []).map((def) => (
                    <div key={def.id} className="grid gap-2">
                      <Label htmlFor={`prop-${def.id}`} className="text-sm">
                        {def.name}
                      </Label>
                      {def.type === "text" && (
                        <Input
                          id={`prop-${def.id}`}
                          placeholder={`${def.name} (opcional)`}
                          value={(propertyFieldValues[def.id] as string) ?? ""}
                          onChange={(e) =>
                            setPropertyFieldValues((prev) => ({
                              ...prev,
                              [def.id]: e.target.value,
                            }))
                          }
                        />
                      )}
                      {def.type === "select" && (
                        <Select
                          value={(propertyFieldValues[def.id] as string) ?? ""}
                          onValueChange={(val) =>
                            setPropertyFieldValues((prev) => ({ ...prev, [def.id]: val }))
                          }
                        >
                          <SelectTrigger id={`prop-${def.id}`}>
                            <SelectValue placeholder="Selecionar..." />
                          </SelectTrigger>
                          <SelectContent>
                            {(def.options ?? []).map((opt) => (
                              <SelectItem key={opt.id} value={opt.id}>
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      {def.type === "multi_select" && (
                        <div className="flex flex-col gap-1">
                          {(def.options ?? []).map((opt) => (
                            <div key={opt.id} className="flex items-center gap-2">
                              <Checkbox
                                id={`prop-${def.id}-${opt.id}`}
                                checked={((propertyFieldValues[def.id] as string[]) ?? []).includes(
                                  opt.id,
                                )}
                                onCheckedChange={(checked) => {
                                  const current = (propertyFieldValues[def.id] as string[]) ?? [];
                                  setPropertyFieldValues((prev) => ({
                                    ...prev,
                                    [def.id]: checked
                                      ? [...current, opt.id]
                                      : current.filter((id) => id !== opt.id),
                                  }));
                                }}
                              />
                              <Label htmlFor={`prop-${def.id}-${opt.id}`}>{opt.label}</Label>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </>
              )}
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsAddApplicationOpen(false)}
                  data-testid="button-cancel-add-application"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={addApplicationMutation.isPending}
                  data-testid="button-submit-application"
                >
                  {addApplicationMutation.isPending ? "Adicionando..." : "Adicionar"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <Dialog open={isCloseJobOpen} onOpenChange={setIsCloseJobOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Fechar Vaga</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja fechar a vaga <strong>{kanban.title}</strong>? A vaga não
              aparecerá mais na página de carreiras, mas você ainda poderá visualizá-la
              internamente.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsCloseJobOpen(false)}
              data-testid="button-cancel-close-job"
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={() => closeJobMutation.mutate()}
              disabled={closeJobMutation.isPending}
              data-testid="button-confirm-close-job"
            >
              {closeJobMutation.isPending ? "Fechando..." : "Fechar Vaga"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit stages and edit job dialogs removed — consolidated into JobSettingsDialog */}
      {jobId && (
        <PropertyConfigSheet
          open={isPropertyConfigOpen}
          onOpenChange={setIsPropertyConfigOpen}
          jobOpeningId={jobId}
          definitions={kanban?.propertyDefinitions ?? []}
          queryKey={kanbanQueryKey}
        />
      )}
      <JobSettingsDialog
        open={isSettingsDialogOpen}
        onOpenChange={(open) => {
          if (!open) removeParam("settings-tab");
        }}
        defaultTab={settingsTab ?? "settings"}
        onTabChange={(tab) => setParam("settings-tab", tab)}
        isSuperAdmin={isSuperAdmin}
        jobOpeningId={jobId ?? ""}
        careersVisibility={{
          checked: !kanban?.hiddenFromCareers,
          onToggle: () => toggleHiddenMutation.mutate(),
          isPending: toggleHiddenMutation.isPending,
          testId: "switch-careers-visibility",
        }}
        autoAdvance={{
          checked: kanban?.allowAutoAdvance ?? false,
          onToggle: () => toggleAutoAdvanceMutation.mutate(),
          isPending: toggleAutoAdvanceMutation.isPending,
          testId: "switch-auto-advance",
        }}
        feedbackAlert={{
          checked: kanban?.notifyHmOnFeedback ?? false,
          onToggle: () => toggleNotifyHmFeedbackMutation.mutate(),
          isPending: toggleNotifyHmFeedbackMutation.isPending,
          testId: "switch-notify-hm-feedback",
        }}
        dailyDigest={{
          checked: kanban?.notifyHmDailyDigest ?? false,
          onToggle: () => toggleNotifyHmDigestMutation.mutate(),
          isPending: toggleNotifyHmDigestMutation.isPending,
          testId: "switch-notify-hm-digest",
          schedule: digestSchedule,
          isSchedulePending: addDigestHourMutation.isPending || deleteDigestEntryMutation.isPending,
          onAddHour: (hour: number) => addDigestHourMutation.mutate(hour),
          onDeleteEntry: (id: string) => deleteDigestEntryMutation.mutate(id),
          onApplyToAll: () => applyDigestToAllMutation.mutate(),
          isApplyToAllPending: applyDigestToAllMutation.isPending,
          hasMultipleJobs: true,
        }}
        newApplicationAlert={{
          checked: kanban?.notifyHmOnNewApplication ?? false,
          onToggle: () => toggleNotifyHmNewApplicationMutation.mutate(),
          isPending: toggleNotifyHmNewApplicationMutation.isPending,
          testId: "switch-notify-hm-new-application",
        }}
        offerApproval={{
          rules: kanban?.offerApprovalRules ?? null,
          onSave: (rules) => updateOfferApprovalRulesMutation.mutate(rules),
          isPending: updateOfferApprovalRulesMutation.isPending,
        }}
        editJobForm={editJobForm}
        onUpdateJob={(data) => updateJobMutation.mutate(data)}
        updateJobIsPending={updateJobMutation.isPending}
        people={people}
        departments={DEPARTMENTS.map((dept) => ({ value: dept.value, label: dept.label }))}
        jobLevels={JOB_LEVELS.map((level) => ({ value: level.value, label: level.label }))}
        editableStages={editableStages}
        setEditableStages={setEditableStages}
        expandedStages={expandedStages}
        setExpandedStages={setExpandedStages}
        editStageInterviewerSearch={editStageInterviewerSearch}
        setEditStageInterviewerSearch={setEditStageInterviewerSearch}
        onUpdateStages={(stages) => updateStagesMutation.mutate(stages)}
        updateStagesIsPending={updateStagesMutation.isPending}
        onValidateAndSaveStages={() => {
          const visibleStages = editableStages.filter((s) => s.stageName.trim());
          if (visibleStages.length === 0) {
            toast({
              title: "Erro",
              description: "Adicione pelo menos uma etapa.",
              variant: "destructive",
            });
            return;
          }
          const invalidGroup = visibleStages.some((s) =>
            (s.interviewerGroups || []).some((g) => g.employeeIds.length < 2),
          );
          if (invalidGroup) {
            toast({
              title: "Erro",
              description: "Todos os grupos precisam de pelo menos 2 membros.",
              variant: "destructive",
            });
            return;
          }
          updateStagesMutation.mutate(visibleStages);
        }}
        archiveDialogStage={archiveDialogStage}
        setArchiveDialogStage={setArchiveDialogStage}
        archiveAction={archiveAction}
        setArchiveAction={setArchiveAction}
        archiveTargetStageId={archiveTargetStageId}
        setArchiveTargetStageId={setArchiveTargetStageId}
        onArchiveStage={(params) => archiveStageMutation.mutate(params)}
        onArchiveInfoCheck={(stageId) => {
          archiveInfoMutation.reset();
          archiveInfoMutation.mutateAsync(stageId).then((info) => {
            if (info.interviewCount === 0 && info.applicationCount === 0) {
              archiveStageMutation.mutate({ stageId, action: "keep" });
            } else {
              const stage = editableStages.find((s) => s.id === stageId);
              setArchiveDialogStage({ id: stageId, name: stage?.stageName ?? "" });
            }
          });
        }}
        archiveInfoMutation={archiveInfoMutation}
        deletingStageId={deletingStageId}
        archivedInterviewStages={kanban?.interviewStages.filter((s) => !!s.archivedAt) ?? []}
        onUnarchiveStage={(stageId) => unarchiveStageMutation.mutate(stageId)}
        unarchiveIsPending={unarchiveStageMutation.isPending}
      />
    </div>
  );
}
